<!--
@component
Match

See markup below.
-->

<script lang="ts">
  import type { Match } from '$lib/@types/opta';
  import { appState } from '$lib/stores/index.svelte';
  import { page } from '$app/state';
  import { onMount } from 'svelte';
  import AnimatedScore from '$lib/components/common/AnimatedScore/index.svelte';
  import LiveBug from '$lib/components/common/LiveBug/index.svelte';
  import Flag from '$lib/components/common/Flag/index.svelte';
  import { isMatchSoon } from '$utils/dates';
  import {
    getHomeTeam,
    getAwayTeam,
    isUpcoming,
    isPlaying,
    getHomeScore,
    getAwayScore,
    isOvertime,
  } from '$utils/getMatchStats';

  type Props = { match: Match };
  let { match }: Props = $props();

  // The `localDateStamp` decoration is computed in the viewer's local
  // timezone, so its values can differ between SSR and the hydrated
  // client render. Wait until the component has mounted on the client
  // before showing real date/time values to avoid a visible shift.
  let mounted = $state(false);
  onMount(() => {
    mounted = true;
  });

  // Match state
  const isFixture = $derived(isUpcoming(match));
  const isLive = $derived(isPlaying(match));
  const overtime = $derived(isOvertime(match));

  // Teams & scores
  const homeTeam = $derived(getHomeTeam(match));
  const awayTeam = $derived(getAwayTeam(match));
  const homeScore = $derived(getHomeScore(match));
  const awayScore = $derived(getAwayScore(match));

  // Whether we're on the schedule page (compact: time-only).
  const onSchedulePage = $derived(page.url.pathname.includes('schedule'));

  // Show only the time (instead of a date) when the match is today
  // or kicks off within the next 12 hours.
  const showTimeOnly = $derived(isMatchSoon(match));

  // Prefer the `localDateStamp` decorated onto each match by `getData()`
  // (in `$utils/data/endpoints.js`) so we don't re-do date math here.
  // Fall back to `null` if the field is missing (e.g. partial fixtures).
  const dateParts = $derived(match?.localDateStamp ?? null);
</script>

<div
  class="card relative font-sans flex flex-row items-center justify-between"
  style="--h: 3rem;"
  class:loading={appState.isLoading}
  class:isFixture
  class:isLive
>
  <!-- Home team (left) -->
  <div
    class="team home-team flex items-center justify-start"
    class:lead={homeScore?.score + homeScore?.pen >
      awayScore?.score + awayScore?.pen}
  >
    <div class="flag relative" class:loading={appState.isLoading}>
      <Flag
        circle={true}
        link={false}
        width="var(--h)"
        country={homeTeam?.name}
      />
    </div>
    <span class="team-name text-left text-sm font-medium tracking-wider">
      {homeTeam?.code || 'TBD'}
    </span>
  </div>

  {#if isFixture}
    <!-- Fixture: date/time -->
    <div
      class="schedule text-xs text-center font-medium tracking-wide text-secondary"
      class:time={onSchedulePage || showTimeOnly}
      class:date={!onSchedulePage && !showTimeOnly}
    >
      {#if !mounted || appState.isLoading || !dateParts}
        &hellip;
      {:else if onSchedulePage || showTimeOnly}
        {dateParts.time}
      {:else}
        {dateParts.month}
        {dateParts.day}
      {/if}
    </div>
  {:else}
    <!-- Live/finished: scores -->
    <div class="results relative flex flex-col items-center">
      {#if isLive}
        <div class="live-container">
          <LiveBug />
        </div>
      {/if}
      <div
        class="scores flex flex-row items-center justify-center"
        class:isLive
      >
        <div
          class="score-home score flex items-center font-bold text-lg"
          class:lead={homeScore?.score + homeScore?.pen >
            awayScore?.score + awayScore?.pen}
        >
          <AnimatedScore value={homeScore?.score ?? 0} />
          {#if homeScore?.pen}
            <div class="pen fml-1 text-base font-regular">
              ({homeScore.pen})
            </div>
          {/if}
        </div>

        {#if overtime}
          <div class="score-overtime text-xs">
            <span class="font-bold fpx-1 text-base">&middot;</span>AET
            <span class="font-bold fpx-1 text-base">&middot;</span>
          </div>
        {:else}
          <div class="score-separator font-bold fpx-2">&middot;</div>
        {/if}

        <div
          class="score-away score flex items-center font-bold text-lg"
          class:lead={awayScore?.score + awayScore?.pen >
            homeScore?.score + homeScore?.pen}
        >
          {console.log(awayScore)}
          <AnimatedScore value={awayScore?.score ?? 0} />
          {#if awayScore?.pen}
            <div class="pen fml-1 text-base font-regular">
              ({awayScore.pen})
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- Away team (right) -->
  <div
    class="team away-team flex items-center justify-end"
    class:lead={awayScore?.score + awayScore?.pen >
      homeScore?.score + homeScore?.pen}
  >
    <span class="team-name text-right text-sm font-medium tracking-wider">
      {awayTeam?.code || 'TBD'}
    </span>
    <div class="flag relative" class:loading={appState.isLoading}>
      <Flag
        circle={true}
        link={false}
        width="var(--h)"
        country={awayTeam?.name}
      />
    </div>
  </div>
</div>

<style lang="scss">
  @use '../../../../styles/mixins' as mixins;

  .card {
    background-color: var(--theme-colour-muted-grey);
    width: 100%;
    height: var(--h);
    border: 1px solid var(--theme-colour-muted-grey);
    border-radius: calc(10 * var(--theme-style-border-radius));
    &:before {
      content: '';
      position: absolute;
      border-radius: calc(10 * var(--theme-style-border-radius));
      mix-blend-mode: multiply;
      width: 100%;
      height: 100%;
    }
    &.loading {
      &:before {
        @include mixins.loading;
      }
    }

    &.isLive {
      @include mixins.hard-shadow;
      // Raise the live card above sibling strips so the LiveBug,
      // which overflows the card's bottom edge, isn't hidden behind
      // the next strip stacked below it.
      z-index: 2;
      &:before {
        @include mixins.loading;
      }
    }

    &:not(.isFixture) {
      background-image: url('$images/branding/match-card-texture.svg');
      background-blend-mode: multiply;
      background-position: 50% 2%;
      background-repeat: no-repeat;
      background-size: 150%;
    }
  }

  .team {
    gap: clamp(0.56rem, 0.52rem + 0.21vw, 0.69rem);
  }

  .team-name {
    font-variant-numeric: tabular-nums;
    min-width: 3rem;
  }

  .flag {
    width: var(--h);
    aspect-ratio: 1;
    border-radius: calc(10 * var(--theme-style-border-radius));
    outline: 1px solid var(--theme-colour-white);

    // `Flag` renders the inlined base64 fallback PNG until the real
    // flag decodes; no need for another network `UNKNOWN.png` underneath.
    &.loading {
      background-color: var(--theme-colour-tan-grey);
    }

    :global(img) {
      border-radius: var(--theme-style-border-radius-pill);
    }
  }

  .scores {
    font-variant-numeric: tabular-nums;

    &.isLive {
      .score {
        color: var(--theme-colour-live-red);
      }
    }
  }

  .live-container {
    position: absolute !important;
    top: -0.75rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
  }
</style>
